export type ResponseStatus =
  | 'Substantive'
  | 'Objection Only'
  | 'References Documents'
  | 'Amended'
  | 'Not Yet Received'

export interface DiscoveryResponse {
  id: string
  party: string
  set: string
  type: 'Form Interrogatories' | 'Special Interrogatories'
  number: string
  question: string
  response: string
  statuses: ResponseStatus[]
}

export const PARTIES = [
  'Defendant 1 — Acme Corp',
  'Defendant 2 — Smith Holdings',
  'Defendant 3 — Pacific Industries',
  'Defendant 4 — Global Logistics',
]

export const SETS = ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5']
export const TYPES = ['Form Interrogatories', 'Special Interrogatories']
export const STATUSES: ResponseStatus[] = [
  'Substantive',
  'Objection Only',
  'References Documents',
  'Amended',
  'Not Yet Received',
]

export const mockData: DiscoveryResponse[] = [
  {
    id: '1',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 1',
    question:
      'State all facts upon which you base your contention that the product was manufactured in accordance with industry standards.',
    response:
      'Responding Party objects to this interrogatory on the grounds that it is overly broad, unduly burdensome, and seeks information protected by the attorney-client privilege. Subject to and without waiving these objections, Responding Party states that the product underwent rigorous testing pursuant to ISO 9001 standards.',
    statuses: ['Objection Only', 'Substantive'],
  },
  {
    id: '2',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 8.1',
    question:
      'Identify all persons who assisted in the preparation of these responses.',
    response:
      'Jane Doe, General Counsel for Acme Corp; John Smith, Director of Engineering.',
    statuses: ['Substantive'],
  },
  {
    id: '3',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 14',
    question:
      'Identify all documents relating to the design, testing, and modification of the braking mechanism.',
    response: 'See documents produced at ACME_001452 through ACME_001983.',
    statuses: ['References Documents'],
  },
  {
    id: '4',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 4',
    question:
      'Describe in detail the reporting structure of the quality assurance department from 2018 to 2021.',
    response:
      "Objection. Vague and ambiguous as to 'reporting structure'. Furthermore, this request is not reasonably calculated to lead to the discovery of admissible evidence.",
    statuses: ['Objection Only'],
  },
  {
    id: '5',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 15.1',
    question:
      'Identify each denial of a material allegation and each special or affirmative defense in your pleadings.',
    response:
      'Responding Party incorporates by reference its First Amended Answer filed on March 12, 2023.',
    statuses: ['References Documents', 'Amended'],
  },
  {
    id: '6',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 7',
    question:
      'State the date on which you first became aware of the alleged defect in the Model X-100.',
    response:
      'Responding Party first received a customer complaint regarding the Model X-100 on or about November 14, 2019.',
    statuses: ['Substantive'],
  },
  {
    id: '7',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 22',
    question:
      'Identify all communications between your engineering team and Acme Corp regarding the safety specifications.',
    response:
      'Objection. Overly broad and seeks confidential business information. Subject to the protective order, see PAC_004511 to PAC_005022.',
    statuses: ['Objection Only', 'References Documents'],
  },
  {
    id: '8',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 4.1',
    question:
      'At the time of the INCIDENT, was there in effect any policy of insurance through which you were or might be insured?',
    response:
      'Yes. Commercial General Liability Policy No. GL-99821 issued by Travelers Insurance.',
    statuses: ['Substantive'],
  },
  {
    id: '9',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 2',
    type: 'Special Interrogatories',
    number: 'Special No. 11',
    question:
      'Describe the chain of custody for the shipment of the Model X-100 units from the manufacturing facility to the distribution center.',
    response:
      'The units were loaded onto Fleet Vehicle 44 on October 2, 2020, and arrived at the distribution center on October 5, 2020. See shipping manifests GL_00210-GL_00215.',
    statuses: ['Substantive', 'References Documents'],
  },
  {
    id: '10',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 45',
    question:
      'Explain the rationale for changing the material of the casing from aluminum to polymer in the 2021 redesign.',
    response:
      'Objection. Assumes facts not in evidence and mischaracterizes the nature of the redesign. The polymer was selected for its superior thermal resistance properties.',
    statuses: ['Objection Only', 'Substantive', 'Amended'],
  },
  {
    id: '11',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 17.1',
    question:
      'Is your response to each request for admission served with these interrogatories an unqualified admission?',
    response: 'No. See responses to Requests for Admission, Set 2.',
    statuses: ['References Documents'],
  },
  {
    id: '12',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 33',
    question:
      'Identify all prior lawsuits in which you have been named as a defendant alleging product liability for similar devices.',
    response:
      'Objection. Irrelevant and overly burdensome. Responding Party declines to answer.',
    statuses: ['Objection Only'],
  },
  {
    id: '13',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 19',
    question:
      'Did any of your employees report damage to the packaging of the Model X-100 units upon arrival?',
    response: '',
    statuses: ['Not Yet Received'],
  },
  {
    id: '14',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 12.1',
    question:
      'State the name, ADDRESS, and telephone number of each individual who witnessed the INCIDENT.',
    response:
      'Responding Party was not present at the incident and has no personal knowledge of any witnesses other than those identified in the police report.',
    statuses: ['Substantive'],
  },
  {
    id: '15',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 28',
    question:
      'Detail the financial relationship between Smith Holdings and Acme Corp during the fiscal year 2020.',
    response:
      'Objection. Seeks proprietary financial data. Subject to the objection, Smith Holdings held a 15% equity stake in Acme Corp during the specified period.',
    statuses: ['Objection Only', 'Substantive'],
  },
  {
    id: '16',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 1',
    type: 'Form Interrogatories',
    number: 'Form 2.6',
    question:
      'State your present residence ADDRESS, and all other residence ADDRESSES for the past five years.',
    response: 'Objection. Interrogatory is inapplicable to a corporate entity.',
    statuses: ['Objection Only'],
  },
  {
    id: '17',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 2',
    question: 'Identify the driver of Fleet Vehicle 44 on October 2, 2020.',
    response:
      'Michael Chang, employed by Global Logistics from 2018 to present.',
    statuses: ['Substantive'],
  },
  {
    id: '18',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 50',
    question:
      'Admit that the internal testing conducted in August 2019 revealed a failure rate of 4.2%.',
    response:
      'Objection. This is improperly framed as a request for admission. Notwithstanding the objection, the August 2019 report speaks for itself. See ACME_008991.',
    statuses: ['Objection Only', 'References Documents'],
  },
  {
    id: '19',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 9',
    question:
      'Who authorized the release of the marketing materials attached as Exhibit A to the Complaint?',
    response:
      'The marketing materials were approved by the joint marketing committee, comprising representatives from both Smith Holdings and Acme Corp.',
    statuses: ['Substantive', 'Amended'],
  },
  {
    id: '20',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 16.1',
    question:
      'Do you contend that any person, other than you or plaintiff, contributed to the occurrence of the INCIDENT?',
    response:
      "Yes. Responding Party contends that Plaintiff's misuse of the product was the sole proximate cause of the incident.",
    statuses: ['Substantive'],
  },
  {
    id: '21',
    party: 'Defendant 4 — Global Logistics',
    set: 'Set 2',
    type: 'Form Interrogatories',
    number: 'Form 8.2',
    question:
      'If you claim that any person is responsible for the INCIDENT, identify each such person.',
    response: '',
    statuses: ['Not Yet Received'],
  },
  {
    id: '22',
    party: 'Defendant 1 — Acme Corp',
    set: 'Set 1',
    type: 'Special Interrogatories',
    number: 'Special No. 12',
    question:
      'Describe the process by which quality control audits were conducted on the assembly line.',
    response:
      'Audits were conducted randomly on 5% of all units produced daily by the shift supervisor, following the checklist in the QC Manual (ACME_000112).',
    statuses: ['Substantive', 'References Documents'],
  },
  {
    id: '23',
    party: 'Defendant 2 — Smith Holdings',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 35',
    question:
      'Identify all board meetings where the safety of the Model X-100 was discussed.',
    response:
      'Objection. Protected by attorney-client privilege and work product doctrine as counsel was present to provide legal advice regarding potential liability.',
    statuses: ['Objection Only'],
  },
  {
    id: '24',
    party: 'Defendant 3 — Pacific Industries',
    set: 'Set 3',
    type: 'Special Interrogatories',
    number: 'Special No. 40',
    question:
      'State the total number of Model X-100 units manufactured between January 1, 2020, and December 31, 2020.',
    response: 'Approximately 14,500 units.',
    statuses: ['Substantive', 'Amended'],
  },
]
